import React, { useState, useEffect } from 'react';
import { RouteComponentProps, Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Breadcrumbs,
  MenuItem,
  Typography
} from '@material-ui/core';
import MaterialTable from 'material-table';
import { MoreVert as MoreIcon } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';

import StudentLayout from '../../../../../layouts/StudentLayout';
import Announcement from '../../../../../models/Announcement';
import CourseWork from '../../../../../models/CourseWork';
import StudentSubmission from '../../../../../models/StudentSubmission';
import Course from '../../../../../models/Course';
import utils from '../../../../../libs/utils';
import auth from '../../../../../libs/auth';
import Spec from '../../../../../models/Spec';
import { MenuIconButton } from '../../../../../components/Buttons';

interface StudentCourseWork extends CourseWork {
  submission?: StudentSubmission
}

const Assignments = (props: RouteComponentProps<{ course_id: string }>) => {
  const { course_id: courseId } = props.match.params;
  const [course, setCourse] = useState<string | undefined | null>(null);
  const [courseWorks, setCourseWorks] = useState<StudentCourseWork[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  const loadCourse = async () => {
    const session = auth.getSession();

    const moodleCourses = await Course.getMoodleCourses({
      token: session?.accessToken,
    });

    console.log(moodleCourses);

    const _course = moodleCourses.find((obj) => obj.id === courseId);

    setCourse(_course?.fullname);
  };

  const loadCourseWorks = async () => {
    setCourseWorks([]);
    const session = auth.getSession();

    // Get all courseworks that the student has access to.
    const moodleCourseAssignments = await CourseWork.getMoodleCourseAssignments(
      {
        token: session?.accessToken,
        courseId,
        userId: auth.getSession()?.user?.userid.toString(),
      }
    );

    // Function used to make sure all courseworks shown are not empty.
    const excludeEmptyCoursework = async (courseWork: StudentCourseWork) => {
      const specs = await Spec.find({
        assignmentID: courseWork.id,
      });

      return specs.length > 0;
    };

    const filteredAssignments = [];

    // Filter through all courseworks
    for (const assignment of moodleCourseAssignments) {
      const shouldInclude = await excludeEmptyCoursework(assignment);
      if (shouldInclude) {
        filteredAssignments.push(assignment);
      }
    }
    const sortedByName = filteredAssignments.sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    setCourseWorks(sortedByName);

    // setCourseWorks(
    //   data
    //     .map((courseWork, index) => {
    //       const studentCourseWork = courseWork as StudentCourseWork;
    //       studentCourseWork.submission = (() => {
    //         for (let submission of submissions[index]) {
    //           if (session && submission.userId === session.accountID)
    //             return submission;
    //         }
    //       })();
    //       return studentCourseWork;
    //     })
    //     .filter((a) => a.associatedWithDeveloper)
    // );
  };

  const loadAnnouncements = async () => {
    let list = await Announcement.getAnnouncments();
    setAnnouncements(list);
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      // await Promise.all([loadCourse(), loadCourseWorks(), loadAnnouncements()]);
      await Promise.all([loadCourse(), loadCourseWorks()]);

    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = (date: string) => {
    if (new Date().getTimezoneOffset() === -480) {
      return new Date(date) > utils.offsetDateToUS(new Date().toString());
    }
    return new Date(date) > new Date(); // For US TA's
  };

  useEffect(() => {
    (async () => await loadData())();
  }, []);

  return (
    <StudentLayout>
      <div className="py-4">
        {(() => {
          if (isLoading)
            return (
              <div className="overlay-loader">
                <div className="text-center p-5">
                  <CircularProgress />
                  <p className="m-0">Loading Assignments</p>
                </div>
              </div>
            );

          if (errorMessage)
            return (
              <div className="text-center p-5">
                <h2 className="m-0">Error Loading Assignments</h2>
                <p>{errorMessage}</p>
                <Button
                  type="button"
                  onClick={loadCourseWorks}
                  color="primary"
                  className="mt-3"
                  variant="contained"
                >
                  Try Again
                </Button>
              </div>
            );

          return (
            <>
              {announcements.map((a) =>
                (a.courseID === courseId || a.courseID === '*') &&
                isActive(a.expiration) ? (
                  <Alert
                    severity="info"
                    color="info"
                    style={{ margin: '7px 0 7px 0' }}
                  >
                    {a.message}
                  </Alert>
                ) : null
              )}

              <MaterialTable
                style={{ minHeight: '60vh' }}
                title={
                  <Box pt={2}>
                    <Typography variant="h5">Assignments</Typography>
                    <Breadcrumbs aria-label="breadcrumb">
                      <Link to={Course.getListUrlPath('student')}>Courses</Link>
                      <Typography>{course || courseId}</Typography>
                      <Typography>Assignments</Typography>
                    </Breadcrumbs>
                  </Box>
                }
                data={courseWorks}
                options={{
                  search: false,
                  paging: false,
                  headerStyle: {
                    fontWeight: 'bold',
                  },
                  thirdSortClick: false,
                }}
                columns={[
                  {
                    title: 'Name',
                    field: 'name',
                    render(courseWork) {
                      return (
                        <Link
                          to={`/student/courses/${courseId}/assignments/${courseWork.id}`}
                        >
                          {courseWork.name}
                        </Link>
                      );
                    },
                  },
                  {
                    title: 'Max Grade',
                    field: 'grade',
                    render(courseWork) {
                      // use to show grade: courseWork.grade;
                      return '100';
                    },
                  },
                  {
                    title: 'Actions',
                    width: '10%',
                    render(courseWork) {
                      return (
                        <MenuIconButton icon={<MoreIcon />}>
                          {() => [
                            <MenuItem
                              component="a"
                              target="_blank"
                              href={
                                `https://learn.devleague.com/mod/assign/view.php?id=${courseWork.cmid}`
                              }
                              style={{
                                textDecoration: 'none'
                              }}
                            >
                              View Exercise in Moodle
                            </MenuItem>,
                          ]}
                        </MenuIconButton>
                      );
                    },
                  },
                ]}
              />
            </>
          );
        })()}
      </div>
    </StudentLayout>
  );
};

export default Assignments;
